import { useState, useEffect, useContext, useRef } from 'react';
import { ClientContext, useQuery } from 'react-fetching-library';
import qs from 'qs';

import useMetadataUpdate from '@shared/hooks/useMetadataUpdate';
import useDidUpdate from '@shared/hooks/useDidUpdate';
import { readTableState, writeTableState } from '@shared/lib/tableFilters';
import useExportCsv from '@shared/hooks/useExportCsv';
import { getEndpoint, getTokenEndpoint } from '@shared/lib/endpoint';
import { isEmptyFilterValue, isSplitFilterValue } from '@shared/lib/filterValue';
import { QUERY_DELAY } from '@shared/config/queryDelay';
import { useDispatch, useSelector } from 'react-redux';
import { checkIsMaintenanceMode } from '@shared/lib/downForMaintenance';
import { updateIsMaintenanceMode, DEFAULT_TABLES_SORTING } from 'redux_store';
import { TEXT_FILTERS } from '@shared/config/textFilters';
import { useParams } from 'react-router';

export const ITEMS_PER_PAGE = parseInt(window.innerHeight / 60) + 20;

export const updateObjectValues = obj => {
  const updateFn = optionsAPI => {
    const res = [];
    Object.keys(optionsAPI).forEach(key => {
      if (Array.isArray(optionsAPI[key])) {
        res.push({
          label: key,
          value: key,
          child: optionsAPI[key].map(item => item.key),
        });
        optionsAPI[key].forEach(({ key, color }) => {
          res.push({
            label: key,
            value: key,
            isChild: true,
            color: color,
          });
        });
      } else {
        res.push({
          label: key,
          value: key,
          color: optionsAPI[key],
        });
      }
    });
    return res;
  };

  return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, updateFn(value)]));
};

const useFetchList = ({
  baseEndpoint,
  baseParams,
  exportEndpoint,
  baseFilter,
  customMethod,
  patches,
  tableKey,
  // Session-storage slot for this table's filter and sort. Defaults to
  // `tableKey`; pass a distinct value when two tables share a
  // `tableKey` (column preferences) but must not share filters — the
  // Claims Manager payer tables in portfolio and account mode.
  storageKey = tableKey,
  preferBaseFilter,
  additionalLoads,
  ignorePrefilters,
  canLoad = true,
}) => {
  /*
    items: Array,
    totalCount: Number,
    continuationToken: String
  */
  const client = useContext(ClientContext);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [list, setList] = useState({});
  const [_canLoad, setCanLoad] = useState(canLoad);
  const [sort, setSort] = useState(null);
  const [filter, setFilter] = useState(null);
  const [additionalLoadsResults, setAdditionalLoadsResults] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);
  const filterTimerRef = useRef(null);
  const fetchTimerRef = useRef(null);
  const reloadTimerRef = useRef(null);
  const { patientUuid: paramsPatientUuid } = useParams();
  const isPatientPage = Boolean(paramsPatientUuid) || ignorePrefilters;
  const paramsEndpoint = `${baseEndpoint}${qs.stringify(baseParams, {
    addQueryPrefix: true,
  })}`;

  async function fetchByKey(urlMap) {
    const entries = Object.entries(urlMap); // [['key1', 'url1'], ['key2', 'url2'], ...]
    const results = await Promise.all(
      entries.map(async ([key, request]) => {
        const { payload, error } = await client.query({
          method: 'GET',
          endpoint: request,
        });
        return [key, error ? null : payload];
      })
    );
    const updated = updateObjectValues(Object.fromEntries(results));
    return updated; // { key1: data1, key2: data2, ... }
  }

  const clearEmptyFilter = newFilter => {
    let filter = [...newFilter];
    return filter.filter(({ value }) => {
      // An advanced filter's `{ include, exclude }` value is an object,
      // so it is truthy even when both lists are empty — without this
      // branch a cleared filter would linger as an entry that sends no
      // term but still reads as "filtered" everywhere else.
      if (isSplitFilterValue(value)) {
        return !isEmptyFilterValue(value);
      }
      if (Array.isArray(value)) {
        return value.length > 0 && value.join('').replace(' ', '').length > 0;
      } else {
        return value;
      }
    });
  };
  /*
    endpoint: String
    fetchAll: Boolean - load all the items (check all checkbox)
    pagesToLoad: Number - number of pages to load immediately
      (remove item, scroll down using scrollbar)
  */
  const [params, setParams] = useState({
    endpoint: '',
    fetchAll: false,
    pagesToLoad: 1,
  });
  const { endpoint, fetchAll, pagesToLoad } = params;

  const {
    metadata: { currentAccount },
  } = useSelector(state => state.user);
  const userUuid = useSelector(state => state.user?.user?.uuid);

  const getParams = patches => ({
    method: customMethod ?? 'GET',
    endpoint,
    body: patches,
    // List data must never be served from the client's 3s GET cache:
    // post-mutation reloads (no-show, resend, delete...) fire ~600 ms
    // after the action, and a cache hit from a recent list GET would
    // return the pre-mutation rows — the table would visibly not update.
    skipCache: true,
  });

  const { query, loading } = useMetadataUpdate(useQuery, [getParams(patches), false]);

  const { fetchExportItems, exportItems, exportItemsLoadingStatus } = useExportCsv({
    baseEndpoint: exportEndpoint,
    baseParams: {
      ...baseParams,
      fields: undefined,
    },
    sort,
    filter,
  });

  // load filter/sort params from the table-filter store (whole session, every tab)
  useEffect(() => {
    let { sort, filter = baseFilter } = isPatientPage
      ? {}
      : readTableState(userUuid, currentAccount?.uuid, storageKey);

    let _filter = preferBaseFilter ? baseFilter || filter || [] : filter || [];
    if (
      (!sort || !sort.length || !sort[0].id) &&
      Object.keys(DEFAULT_TABLES_SORTING).includes(tableKey)
    ) {
      sort = [DEFAULT_TABLES_SORTING[tableKey]];
    }
    setSort(sort);
    setFilter(_filter);
    if (canLoad) {
      // waiting fields
      const ep = getEndpoint(baseEndpoint, baseParams, sort, _filter);
      setParams({
        ...params,
        endpoint: ep,
      });
    }
  }, []);

  useEffect(() => {
    if (!_canLoad && canLoad) {
      setCanLoad(true);
      setParams({
        ...params,
        endpoint: getEndpoint(baseEndpoint, baseParams, sort, filter),
      });
    }
  }, [canLoad]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearTimeout(filterTimerRef.current);
      clearTimeout(fetchTimerRef.current);
      clearTimeout(reloadTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (list.items) {
      setIsLoading(false);
    }
  }, [list.items]);

  // save filter/sort params to the table-filter store
  useEffect(() => {
    if ((sort !== null || filter !== null) && !isPatientPage) {
      writeTableState(userUuid, currentAccount?.uuid, storageKey, sort, filter);
    }
  }, [sort, filter]);

  /*
    Clear filters and fetch new list on current account changing
    or on base endpoint changing.
    Need to trigger an endpoint changing even if it's the same.
    Case: accounts list
  */
  useDidUpdate(() => {
    const { sort, filter = baseFilter } = isPatientPage
      ? {}
      : readTableState(userUuid, currentAccount?.uuid, storageKey);
    let newFilter = preferBaseFilter ? baseFilter || filter || [] : filter || [];
    const newSort = sort || [];
    const newEndpoint = getEndpoint(baseEndpoint, baseParams, newSort, newFilter);

    setList({});
    setSort(newSort);
    setFilter(newFilter);
    setParams({
      endpoint: newEndpoint,
      fetchAll: false,
      pagesToLoad: 1,
    });
    setFetchKey(k => k + 1);
  }, [currentAccount.uuid, paramsEndpoint]);

  const fetchNewData = () => {
    const setNewEndpoint = token => {
      setParams({
        ...params,
        endpoint: getTokenEndpoint(baseEndpoint, token),
      });
    };

    const fetchData = async () => {
      if (endpoint) {
        if (!additionalLoadsResults) {
          const res = await fetchByKey(additionalLoads || {});
          setAdditionalLoadsResults(res);
        }
        setFetchError(null);
        setIsLoading(true);
        clearTimeout(fetchTimerRef.current);
        fetchTimerRef.current = setTimeout(async () => {
          try {
            const { error, payload, status } = await query(patches);
            if (!error && payload) {
              setFetchError(null);
              const newList = {
                totalCount: list.totalCount,
                totalAccountsCount: list.totalAccountsCount,
                completionRate: list.completionRate,
                noShowRate: list.noShowRate,
                declinedRate: list.declinedRate,
                noShowAndDeclinedRate: list.noShowAndDeclinedRate,
                performanceRate: list.performanceRate,
                ...payload,
                aggregations:
                  params.pagesToLoad === 1
                    ? payload?.aggregations || {}
                    : {
                        ...payload?.aggregations,
                        ...list?.aggregations,
                      },
              };
              if (endpoint.includes('continuation-token')) {
                newList.items = [].concat(list.items, payload.items);
              }

              if (payload?.result) {
                newList.items = payload.result;
              }

              setList(newList);

              // need to load more than 1 page at once
              if (Math.ceil(newList.items.length / ITEMS_PER_PAGE) < pagesToLoad) {
                if (newList.continuationToken && list.totalCount < newList.items.length) {
                  setNewEndpoint(newList.continuationToken);
                } else {
                  setParams({
                    ...params,
                    pagesToLoad: 1,
                  });
                }
                // need to load all the pages
              } else if (fetchAll) {
                if (newList.items.length !== newList.totalCount) {
                  setNewEndpoint(newList.continuationToken);
                } else {
                  setParams({
                    ...params,
                    fetchAll: false,
                  });
                }
              }
            } else {
              setFetchError({
                message: payload?.message || payload?.title || 'Failed to load data',
                status: payload?.status || status,
                correlationUUID: payload?.correlationUUID,
                validationErrors: payload?.validationErrors || null,
                timestamp: new Date().toISOString(),
              });
              setIsLoading(false);
              const response = await checkIsMaintenanceMode();
              if (response.status === 200) {
                dispatch(updateIsMaintenanceMode(true));
              }
            }
          } catch (err) {
            setFetchError({
              message: err?.message || 'Failed to load data',
              status: null,
              correlationUUID: null,
              validationErrors: null,
              timestamp: new Date().toISOString(),
            });
            setIsLoading(false);
          }
        }, 100);
      }
    };

    fetchData();
  };

  // fetch data on endpoint changing
  useEffect(() => {
    fetchNewData();
  }, [endpoint, fetchKey, query]);

  const onSort = newSort => {
    const _sort = newSort(sort);
    setParams({
      ...params,
      endpoint: getEndpoint(baseEndpoint, baseParams, _sort, filter),
    });
    setSort(_sort);
  };

  const onFilter = async (_filter, hasDelay) => {
    // hasDelay - only for text field
    const newFilter = clearEmptyFilter(_filter(filter));
    let _hasDelay = hasDelay || false;
    const prewFilterAsObj = {};
    const newFilterAsObj = {};
    filter.forEach(({ id, value }) => (prewFilterAsObj[id] = value));
    newFilter.forEach(({ id, value }) => (newFilterAsObj[id] = value));

    Object.keys(newFilterAsObj).forEach(newFilterKey => {
      if (newFilterAsObj[newFilterKey] !== prewFilterAsObj[newFilterKey]) {
        if (TEXT_FILTERS.includes(newFilterKey)) {
          _hasDelay = true;
        }
      }
    });

    if (JSON.stringify(filter) !== JSON.stringify(newFilter)) {
      setFilter(newFilter);

      clearTimeout(filterTimerRef.current);

      if (_hasDelay) {
        filterTimerRef.current = setTimeout(() => {
          setParams({
            ...params,
            endpoint: getEndpoint(baseEndpoint, baseParams, sort, newFilter),
          });
        }, QUERY_DELAY);
      } else {
        setParams({
          ...params,
          endpoint: getEndpoint(baseEndpoint, baseParams, sort, newFilter),
        });
      }
    }
  };

  const onMultiColumnFilter = (newFilters, hasDelay) => {
    const newFilter = clearEmptyFilter(newFilters);

    clearTimeout(filterTimerRef.current);
    filterTimerRef.current = setTimeout(
      () => {
        setParams({
          ...params,
          endpoint: getEndpoint(baseEndpoint, baseParams, sort, newFilter),
        });
        setFilter(newFilter);
      },
      hasDelay ? QUERY_DELAY : 0
    );
  };

  const clearFilter = () => {
    const newFilter = [];
    setParams({
      ...params,
      endpoint: getEndpoint(baseEndpoint, baseParams, sort, newFilter),
    });
    setFilter(newFilter);
  };

  const onLoadMoreRows = stopIndex => {
    const token = list.continuationToken;
    if (list.items && stopIndex > list.items.length - 1 && token && !loading) {
      setParams({
        ...params,
        endpoint: getTokenEndpoint(baseEndpoint, token),
        pagesToLoad: Math.ceil((stopIndex + 1) / ITEMS_PER_PAGE),
      });
    }
  };

  const fetchAllItems = () => {
    if (fetchAll) {
      return;
    }
    const token = list.continuationToken;
    if (token) {
      setParams({
        ...params,
        endpoint: getTokenEndpoint(baseEndpoint, token),
        fetchAll: true,
      });
    }
  };

  const reloadData = props => {
    let DELAY = QUERY_DELAY;
    // eslint-disable-next-line no-prototype-builtins
    if (props && props.hasOwnProperty('delay')) {
      DELAY = props.delay;
    }
    const pages = Math.ceil(list?.items?.length / ITEMS_PER_PAGE);
    clearTimeout(reloadTimerRef.current);
    reloadTimerRef.current = setTimeout(() => {
      setParams(prev => ({
        ...prev,
        pagesToLoad: pages,
      }));
      setFetchKey(k => k + 1);
    }, DELAY);
  };

  // Endpoints occasionally return extras alongside the paged items —
  // KPI summaries, header meta, per-request scoping info. Rather than
  // adding a named return per endpoint (which bloats this shared hook
  // with feature-specific concerns), expose the full first-page
  // payload once so callers can pluck what they need. Everything the
  // hook already returns explicitly stays authoritative; consumers
  // should reach for `responsePayload` only for endpoint-specific
  // extras that don't belong here.
  return {
    items: list.items,
    totalAccountsCount: list.totalAccountsCount,
    aggregations: list.aggregations,
    completionRate: list.completionRate,
    noShowRate: list.noShowRate,
    declinedRate: list.declinedRate,
    noShowAndDeclinedRate: list.noShowAndDeclinedRate,
    performanceRate: list.performanceRate,
    totalCount: list.totalCount,
    responsePayload: list,
    onLoadMoreRows,
    fetchAllItems,
    sort,
    onSort,
    filter,
    clearFilter,
    onFilter: onFilter,
    onMultiColumnFilter: onMultiColumnFilter,
    reloadData,
    exportItems,
    fetchExportItems,
    exportItemsLoadingStatus,
    loading: isLoading,
    fetchError,
    additionalLoadsResults: additionalLoadsResults,
  };
};

export default useFetchList;
